import { nationalityName } from '@felix/domain';
import type { Content, TDocumentDefinitions } from 'pdfmake/interfaces.js';
import { formatDay } from '../../format.js';
import { safeFileBase } from '../drills/report-pdf.js';
import {
  pdfFacts,
  pdfHeading,
  pdfTable,
  pdfText,
  type RecordPdfT,
  recordPdf,
} from '../reports/record-pdf.js';
import type { ReportBrand } from '../voyage/source.js';
import { assignmentState } from './crew-profile.js';
import type { CrewAssignment, CrewCertificates, CrewProfileView } from './types.js';

/**
 * Crew Profile "Print" (ADR 0131 §7, Ek 3; `gemi-personel.jpeg` panel 3): kişisel bilgi, süren
 * kontrat, kontratlar ve Joining / Sign-Off (tarih ve liman), belgeler özeti. Ortak kayıt kabuğu
 * (logo, şirket adı, altbilgide basım zamanı ve sayfa); onay bölümü yok (döküm).
 *
 * - **Gemide** yalnız gemiye inen alanlar basılır (Crew ID, uyruk, doğum tarihi ve yeri, cinsiyet;
 *   ADR 0131 §2). İletişim, medeni hal ve acil durum kişisi gemi kopyasında yoktur (KVKK md. 4):
 *   alan gelse de basılmaz.
 * - **Giriş adresi** kart bilgisi değildir; hiçbir PDF'te yer almaz (Ek 1).
 * - Ayrılış notu PDF'e girmez (iç not; veri en aza indirme).
 *
 * pdfmake istemcide, gömülü yazı tipiyle internetsiz çalışır; dil arayüzün dilidir.
 */

export interface CrewProfilePdfInput {
  view: CrewProfileView;
  scope: 'web' | 'ship';
  /** Kişinin belgeleri; okunamadıysa `null` (bölümde not). */
  certificates: CrewCertificates | null;
  brand: ReportBrand | null;
  /** Basım zamanı, UTC ISO. */
  printedAt: string;
  /** Uyruk adının dili. */
  locale: string;
}

const day = (value: string | null | undefined) => (value ? formatDay(value) : '—');

/** "Bodrum (TRBXN)": ad ve kod; biri yoksa öteki; ikisi de yoksa `—`. */
export function portText(name: string | null | undefined, code: string | null | undefined) {
  if (name && code) return `${name} (${code})`;
  return name || code || '—';
}

/** Tarih ve liman tek hücrede: "07.04.2026\nBodrum (TRBXN)". */
const dateAndPort = (date: string | null, name?: string | null, code?: string | null) =>
  date ? `${formatDay(date)}\n${portText(name, code)}` : '—';

export function crewProfilePdf(t: RecordPdfT, input: CrewProfilePdfInput): TDocumentDefinitions {
  const k = (key: string, options?: Record<string, unknown>) => t(`crewProfile.${key}`, options);
  const p = (key: string, options?: Record<string, unknown>) =>
    t(`crewProfile.pdf.${key}`, options);
  const { view, scope } = input;
  const web = scope === 'web';
  const profile = view.profile;
  const today = view.today ?? input.printedAt.slice(0, 10);

  const personal: [string, string][] = [
    [k('fields.fullName'), view.fullName],
    [k('fields.crewCode'), pdfText(profile?.crewCode)],
    [k('fields.rank'), pdfText(view.rank)],
    [k('fields.vessel'), pdfText(view.vessel?.name)],
    [k('fields.nationality'), pdfText(nationalityName(profile?.nationality, input.locale))],
    [k('fields.dateOfBirth'), day(profile?.dateOfBirth)],
    [k('fields.placeOfBirth'), pdfText(profile?.placeOfBirth)],
    [k('fields.gender'), profile?.gender ? k(`gender.${profile.gender}`) : '—'],
  ];
  if (web) {
    personal.push([
      k('fields.maritalStatus'),
      profile?.maritalStatus ? k(`marital.${profile.maritalStatus}`) : '—',
    ]);
  }

  const content: Content[] = [pdfHeading(k('sections.personal')), pdfFacts(personal)];

  if (web) {
    content.push(
      pdfHeading(k('sections.contact')),
      pdfFacts([
        [k('fields.contactEmail'), pdfText(profile?.contactEmail)],
        [k('fields.phone'), pdfText(profile?.phone)],
        [k('fields.address'), pdfText(profile?.address)],
      ]),
      pdfHeading(k('sections.emergency')),
      pdfFacts([
        [k('fields.emergencyName'), pdfText(profile?.emergencyName)],
        [k('fields.emergencyRelation'), pdfText(profile?.emergencyRelation)],
        [k('fields.emergencyPhone'), pdfText(profile?.emergencyPhone)],
      ]),
    );
  } else {
    content.push({ text: p('shipCopy'), style: 'muted', margin: [0, 4, 0, 0] });
  }

  // Süren kontrat: bugün gemide olan atama (ADR 0131 §4)
  const current = view.assignments.find(
    (row) => row.validFrom <= today && (row.validTo === null || row.validTo >= today),
  );
  if (current) {
    content.push(
      pdfHeading(p('currentContract')),
      pdfFacts([
        [p('columns.contractNo'), pdfText(current.contractNo)],
        [p('columns.vessel'), current.vesselName],
        [p('columns.rank'), pdfText(current.rank)],
        [
          p('columns.joined'),
          dateAndPort(current.validFrom, current.joinPort, current.joinPortCode),
        ],
        [p('columns.contractEnd'), day(current.contractEnd)],
        [p('columns.status'), stateText(k, current, today)],
      ]),
    );
  }

  content.push(pdfHeading(p('contracts')));
  if (view.assignments.length === 0) {
    content.push({ text: k('assignments.empty'), style: 'muted' });
  } else {
    content.push(
      pdfTable(
        [
          p('columns.contractNo'),
          p('columns.vessel'),
          p('columns.rank'),
          p('columns.joined'),
          p('columns.signedOff'),
          p('columns.contractEnd'),
          p('columns.status'),
        ],
        view.assignments.map((row) => [
          pdfText(row.contractNo),
          row.vesselName,
          pdfText(row.rank),
          dateAndPort(row.validFrom, row.joinPort, row.joinPortCode),
          dateAndPort(row.validTo, row.signOffPort, row.signOffPortCode),
          day(row.contractEnd),
          stateText(k, row, today),
        ]),
        [52, '*', 62, 72, 72, 50, 48],
      ),
    );
  }

  content.push(pdfHeading(p('documents')));
  const certs = input.certificates;
  if (!certs) {
    content.push({ text: p('documentsUnavailable'), style: 'muted' });
  } else {
    content.push({
      text: p('summary', {
        total: certs.summary.total,
        valid: certs.summary.valid,
        expiring: certs.summary.expiring,
        expired: certs.summary.expired,
      }),
      style: 'muted',
      margin: [0, 0, 0, 3],
    });
    if (certs.certificates.length === 0) {
      content.push({ text: p('noDocuments'), style: 'muted' });
    } else {
      content.push(
        pdfTable(
          [
            p('columns.document'),
            p('columns.number'),
            p('columns.issued'),
            p('columns.expires'),
            p('columns.issuedBy'),
            p('columns.status'),
          ],
          certs.certificates.map((c) => [
            c.name,
            pdfText(c.number),
            day(c.issuedOn),
            day(c.expiresOn),
            pdfText(c.issuedBy),
            p(`docStatus.${c.status}`),
          ]),
          ['*', 80, 54, 54, 70, 60],
        ),
      );
    }
    if (certs.summary.missing.length > 0) {
      content.push({
        text: p('missing', { list: certs.summary.missing.map((m) => m.name).join(', ') }),
        style: 'mark',
        margin: [0, 4, 0, 0],
      });
    }
  }

  return recordPdf(t, {
    brand: input.brand,
    title: p('title'),
    number: [profile?.crewCode, view.fullName].filter(Boolean).join(' · '),
    marks: [],
    subject: view.fullName,
    content,
    approvals: null,
    printedAt: input.printedAt,
  });
}

function stateText(
  k: (key: string, options?: Record<string, unknown>) => string,
  row: CrewAssignment,
  today: string,
): string {
  return k(`assignments.state.${row.contractStatus ?? assignmentState(row, today)}`);
}

/** Dosya adı: "crew-profile-C-045-Gizem-Koc.pdf" (gemide kaydedilebilir ad). */
export function crewProfileFileName(view: Pick<CrewProfileView, 'fullName' | 'profile'>): string {
  const parts = ['crew-profile', view.profile?.crewCode ?? '', view.fullName];
  return `${safeFileBase(parts.filter(Boolean).join('-'))}.pdf`;
}
